/**
 * fillData — glue pure entre <FormRenderer> (émet des valeurs brutes, les nombres
 * en `string`) et le contrat serveur (`buildFormResponseDataSchema` attend
 * `z.number()`, `z.boolean()`, arrays — SANS coercition). Sans cette normalisation,
 * un champ `number` rempli à l'écran partirait en `string` → 422 côté serveur.
 *
 * `toWireData` produit exactement ce qui est envoyé au wire (create/patch) et ce
 * qui est validé côté client (mode submit) → client et serveur voient la même chose.
 * Aucun réseau, aucune dépendance React : testable en isolation.
 */
import { Schemas, requiredFormFieldKeys } from "@kollectivia/shared";

type FormSchema = Parameters<typeof Schemas.buildFormResponseDataSchema>[0];

/**
 * Normalise les valeurs d'écran en payload `data` typé, champ par champ selon le
 * schéma de la version. Les valeurs vides/absentes sont **omises** (brouillon
 * permissif ; requis honoré au submit). Les types non remplissables en `fill`
 * (`file`/`company_ref`/`contact_ref`, cf. dette D-NEW-FORMS-1DII-REF-NO-PICKER)
 * ne sont transmis que défensivement s'ils portent déjà une valeur.
 */
export function toWireData(
  values: Record<string, unknown>,
  schema: FormSchema,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const section of schema.sections) {
    for (const f of section.fields) {
      const v = values[f.key];
      switch (f.type) {
        case "number": {
          if (v === undefined || v === null || v === "") break;
          const n = typeof v === "number" ? v : Number(String(v).trim());
          if (Number.isFinite(n)) out[f.key] = n;
          // Invalide (NaN) → omis : la validation submit signalera « requis » si besoin.
          break;
        }
        case "checkbox":
          // true = coché ; false/absent traités identiquement (omis) → « requis » = doit être coché.
          if (v === true) out[f.key] = true;
          break;
        case "multi_select":
          if (Array.isArray(v) && v.length > 0) out[f.key] = v;
          break;
        case "file":
        case "company_ref":
        case "contact_ref":
          if (typeof v === "string" && v !== "") out[f.key] = v;
          else if (Array.isArray(v) && v.length > 0) out[f.key] = v;
          break;
        default:
          // text / textarea / email / date / select
          if (typeof v === "string" && v !== "") out[f.key] = v;
      }
    }
  }
  return out;
}

/**
 * Issues traduisibles ; la contrainte positive provient du schéma partagé.
 *
 * Une absence, une valeur invalide ou un montant qui doit être supérieur à zéro.
 */
export type FieldIssue = "required" | "invalid" | "positive";

/**
 * Valide le payload wire en **mode submit** (requis obligatoires, types stricts)
 * contre le schéma exact de la version. Retourne une map `clé → issue` (vide si
 * conforme) pour affichage inline via `<FormRenderer errors>`. Aucun effet réseau.
 *
 * ## Pourquoi un CODE et pas un message
 *
 * Cette fonction renvoyait `iss.message` — c'est-à-dire le **message anglais par
 * défaut de Zod**. Un mandat rédigé en français affichait donc « Required » sous
 * ses champs, y compris sur la surface publique, devant le client. Ce n'était pas
 * une clé i18n manquante : aucun texte traduisible n'existait.
 *
 * Renvoyer un code laisse la traduction à l'appelant, qui a le contexte de langue
 * — et rend les tests indépendants de la formulation de Zod.
 */
export function validateSubmitData(
  wire: Record<string, unknown>,
  schema: FormSchema,
): Record<string, FieldIssue> {
  return validateFormData(wire, schema, "submit");
}

/** Drafts allow absent requirements but still reject invalid supplied values. */
export function validateDraftData(
  wire: Record<string, unknown>,
  schema: FormSchema,
): Record<string, FieldIssue> {
  return validateFormData(wire, schema, "draft");
}

function validateFormData(
  wire: Record<string, unknown>,
  schema: FormSchema,
  mode: "draft" | "submit",
): Record<string, FieldIssue> {
  const validator = Schemas.buildFormResponseDataSchema(schema, { mode });
  const parsed = validator.safeParse(wire);
  if (parsed.success) return {};
  const errs: Record<string, FieldIssue> = {};
  for (const iss of parsed.error.issues) {
    const key = String(iss.path[0] ?? "");
    if (!key || errs[key]) continue;
    // Un champ requis absent se présente en `invalid_type` avec `received:
    // undefined` — c'est la seule forme qui veut dire « il manque quelque
    // chose », et c'est celle que la personne peut réparer sans rien comprendre
    // d'autre.
    const manquant =
      iss.code === "invalid_type" && (iss as { received?: unknown }).received === "undefined";
    const positive =
      iss.code === "too_small" && iss.type === "number" && iss.minimum === 0 && !iss.inclusive;
    errs[key] = manquant ? "required" : positive ? "positive" : "invalid";
  }
  if (mode === "draft") return errs;
  // Zod skips superRefine when another field has a fatal type/enum error.
  // Surface all missing active requirements using the same shared predicate;
  // this adds diagnostics only and never relaxes the server's validation.
  const required = requiredFormFieldKeys(schema.sections, wire);
  for (const section of schema.sections) {
    for (const field of section.fields) {
      const value = wire[field.key];
      if (
        required.has(field.key) &&
        (value === undefined ||
          ((field.active_when || field.required_when) &&
            typeof value === "string" &&
            !value.trim()))
      ) {
        errs[field.key] = "required";
      }
    }
  }
  return errs;
}

/**
 * Traduit les issues en messages affichables — chunk 9.BJ.5.
 *
 * Le point de traduction est ici, chez l'appelant qui a le contexte de langue,
 * et pas dans la validation : celle-ci ne doit rien savoir de l'affichage.
 */
export function messagesDIssues(
  issues: Record<string, FieldIssue>,
  t: (cle: string) => string,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [cle, issue] of Object.entries(issues)) {
    out[cle] = t(`forms.field_errors.${issue}`);
  }
  return out;
}
